import React, { useEffect, useMemo, useState } from 'react'
import { GlobalOutlined, PushpinFilled } from '@ant-design/icons'
import { Select } from '@pushly/aqe/lib/components'
import { DashboardDomain } from '../../types'

type Props = {
    domains: DashboardDomain[]
    /** Empty means every domain. */
    selectedDomainIds: number[]
    pinnedDomainIds: number[]
    onCommit: (domainIds: number[]) => void
}

const sameIds = (first: number[], second: number[]): boolean =>
    first.length === second.length && [...first].sort().join(',') === [...second].sort().join(',')

const byDisplayName = (first: DashboardDomain, second: DashboardDomain) =>
    first.displayName.localeCompare(second.displayName)

const optionFor = (domain: DashboardDomain, pinned: boolean) => ({
    value: domain.id,
    label: (
        <span>
            {pinned && <PushpinFilled className="org-dashboard-domain-option-pin" aria-label="Pinned" />}
            {domain.displayName}
        </span>
    ),
    // The hostname is not shown, so a long display name keeps the whole row; a typed search still matches it.
    chipLabel: domain.displayName,
    searchText: `${domain.displayName} ${domain.hostname}`,
})

/**
 * The domain filter. Choices accumulate in a draft while the dropdown is open and are applied once, when it
 * closes, so picking several domains refetches the page once rather than once per click. Selecting every
 * domain is the same as selecting none: both read "All domains".
 */
export const DomainFilterControl: React.FC<Props> = ({ domains, selectedDomainIds, pinnedDomainIds, onCommit }) => {
    const [draftIds, setDraftIds] = useState<number[]>(selectedDomainIds)
    const selectedKey = selectedDomainIds.join(',')
    useEffect(() => setDraftIds(selectedDomainIds), [selectedKey])

    /*
     * Pinned domains come first. aqe's multi-select lists its options flat and does not render option groups, so
     * the pin marker, not a heading, tells the two apart.
     */
    const options = useMemo(() => {
        const sorted = [...domains].sort(byDisplayName)
        const isPinned = (domain: DashboardDomain) => pinnedDomainIds.includes(domain.id)
        return [
            ...sorted.filter(isPinned).map((domain) => optionFor(domain, true)),
            ...sorted.filter((domain) => !isPinned(domain)).map((domain) => optionFor(domain, false)),
        ]
    }, [domains, pinnedDomainIds.join(',')])

    const commitDraft = (open: boolean) => {
        if (open) return
        const filter = draftIds.length === domains.length ? [] : draftIds
        setDraftIds(filter)
        if (!sameIds(filter, selectedDomainIds)) onCommit(filter)
    }

    return (
        <Select
            mode="multiple"
            aria-label="Filter by domain"
            showSelectAll
            selectAllLabel="Select all"
            tagStyle="ghost"
            itemNotation="Domains"
            singleLine
            prefix={<GlobalOutlined />}
            placeholder={`All domains (${domains.length})`}
            options={options}
            optionLabelProp="chipLabel"
            getFilterLabel={(option) => option.searchText}
            // More than one choice reads as a count ("3 Domains") rather than a list of names.
            maxTagCount={draftIds.length > 1 ? 0 : undefined}
            value={draftIds}
            onChange={(ids: number[]) => setDraftIds(ids)}
            onOpenChange={commitDraft}
            // The list is as wide as its longest name rather than the closed control, so no name is cut short.
            popupMatchSelectWidth={false}
            style={{ minWidth: 220, maxWidth: 320 }}
        />
    )
}
